import { Card, Checkbox, IconButton, Menu, MenuItem } from '@material-ui/core';
import PrintIcon from '@material-ui/icons/Print';
import clsx from 'clsx';
import { theme } from 'config/theme';
import { OrderItemStatus, OrderServices, Positions, PrintType } from 'constants/enums';
import i18n from 'i18n-js';
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import ordersStore from 'store/ordersStore';
import { OrderDetailsItem, OrderItem } from 'store/ordersStore/reducer';
import { useStyles } from '../styles';
import { PriceText } from './PriceText';

interface OrderDailyMenuItemProps {
    dailyMenu: OrderDetailsItem;
    dishes: OrderItem[];
    editOrder: boolean;
    editedComment: string | null;
    handleCheckingItems: (
        itemId: string,
        uniqueId: string,
        status: OrderItemStatus,
        dailyMenuId?: string
    ) => void;
    checkIsDisableDailyMenuItem: (dish: OrderItem) => boolean;
    renderDeliveredCheckbox: (dish: OrderItem, dailyMenuId?: string) => React.ReactNode;
    renderEditedIngredients: (dish: OrderItem, dailyMenuId?: string) => React.ReactNode;
    renderComment: (dailyMenu: OrderItem, style?: React.CSSProperties) => React.ReactNode;
    prepareItemStatusClass: (dish: OrderItem, isPaid?: boolean) => string | null;
    HighlitedText: (text: string) => React.ReactNode;
    dailyMenuKey: string;
    selectedFilter: string[];
    restaurant: any;
    orderDetails: any;
    user: any;
    currentPosition: Positions | null;
}

export function OrderDailyMenuItem(props: OrderDailyMenuItemProps) {
    const {
        dailyMenu,
        dishes,
        editOrder,
        editedComment,
        handleCheckingItems,
        checkIsDisableDailyMenuItem,
        renderDeliveredCheckbox,
        renderEditedIngredients,
        renderComment,
        prepareItemStatusClass,
        HighlitedText,
        dailyMenuKey,
        selectedFilter,
        restaurant,
        orderDetails,
        user,
        currentPosition,
    } = props;
    const classes = useStyles();
    const dispatch = useDispatch();
    const hasModifiedIngredients = (dish: OrderItem) =>
        dish.modifiedIngredients && dish.modifiedIngredients?.length > 0;
    const [printItem, setPrintItem] = useState<null | string>(null);
    const [printAnchorEl, setPrintAnchorEl] = useState<null | HTMLElement>(null);

    function handlePrintClick(event: React.MouseEvent<HTMLButtonElement>, item: string) {
        setPrintAnchorEl(event.currentTarget ?? event.target);
        setPrintItem(item);
    }

    function handlePrintClose() {
        setPrintAnchorEl(null);
        setPrintItem(null);
    }

    const handlePrint = (printType: PrintType) => {
        if (printType === PrintType.WholeOrder) {
            dispatch(ordersStore.actionCreators.PrintOrder(orderDetails.id));
        } else if (printType === PrintType.SingleItem) {
            dispatch(ordersStore.actionCreators.PrintOrder(orderDetails.id, [printItem!]));
        }
        handlePrintClose();
    };

    return (
        <div
            style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '100%',
            }}
        >
            <Card
                className={clsx({
                    [classes.disabledMenu]: selectedFilter.includes(dailyMenuKey),
                    [classes.orderItemCardPaid]:
                        dailyMenu?.isPaid &&
                        dailyMenu.dishes.every(
                            (dish) =>
                                dish.status === OrderItemStatus.Order ||
                                dish.status === OrderItemStatus.InProgress
                        ),
                })}
                style={{
                    boxShadow: '0px 2px 8px 0px rgba(0, 0, 0, 0.11)',
                    overflow: 'visible',
                    flexGrow: 1,
                    paddingBottom: editedComment === dailyMenu?.uniqueId ? 8 : 0,
                }}
            >
                {dishes.map((dish, index) => {
                    return (
                        <div key={dish.id + index}>
                            <div
                                className={clsx(
                                    classes.orderItemCard,
                                    classes.orderItemCardDaily,
                                    prepareItemStatusClass(dish, dailyMenu?.isPaid)
                                )}
                                style={{
                                    paddingBottom: editedComment === dish.uniqueId ? 8 : 0,
                                }}
                            >
                                {!editOrder && (
                                    <>
                                        <Checkbox
                                            onChange={() =>
                                                handleCheckingItems(
                                                    dish.id,
                                                    dish.uniqueId!,
                                                    dish.status === OrderItemStatus.Ready
                                                        ? OrderItemStatus.Order
                                                        : OrderItemStatus.Ready,
                                                    dailyMenu?.uniqueId
                                                )
                                            }
                                            inputProps={{
                                                'aria-label': 'select all desserts',
                                            }}
                                            checked={
                                                dish.status === OrderItemStatus.Ready ||
                                                dish.status === OrderItemStatus.Closed ||
                                                dish.status === OrderItemStatus.Delivered
                                            }
                                            disabled={checkIsDisableDailyMenuItem(dish)}
                                        />

                                        {renderDeliveredCheckbox(dish, dailyMenu?.uniqueId)}
                                    </>
                                )}
                                <div className={classes.checkboxContainer}>
                                    <div>
                                        <div
                                            className={clsx(classes.orderItemCardTitle, {
                                                [classes.orderItemCardTitleReady]:
                                                    dish.status === OrderItemStatus.Ready,
                                            })}
                                        >
                                            {editOrder ? dish.name : HighlitedText(dish.name)}
                                            {index === 0 &&
                                                restaurant.advancedSettings?.isPrintingEnabled && (
                                                    <IconButton
                                                        style={{
                                                            marginLeft: 10,
                                                            color:
                                                                dish.status ===
                                                                OrderItemStatus.Ready
                                                                    ? theme.palette.common.white
                                                                    : undefined,
                                                        }}
                                                        onClick={(e) =>
                                                            handlePrintClick(
                                                                e,
                                                                dailyMenu?.uniqueId ??
                                                                    dailyMenu?.id ??
                                                                    ''
                                                            )
                                                        }
                                                    >
                                                        <PrintIcon />
                                                    </IconButton>
                                                )}
                                        </div>
                                        <div className="OrderItemCardDescription">
                                            {!!dish.shortDescription
                                                ? dish.shortDescription
                                                : i18n.t('common.noDescription')}
                                        </div>
                                    </div>
                                    {index === 0 ? (
                                        <>
                                            <PriceText
                                                price={dailyMenu?.price || 0}
                                                isPaid={dailyMenu?.isPaid || orderDetails.isPaid}
                                                itemStatus={dailyMenu?.status}
                                                isDisabled={selectedFilter.includes(dailyMenuKey)}
                                                isStorno={
                                                    dailyMenu?.status === OrderItemStatus.Storno
                                                }
                                            />
                                            {dailyMenu!.amount > 1 && !editOrder && (
                                                <div className={classes.dishAmountAbsolute}>
                                                    {dailyMenu!.amount}
                                                </div>
                                            )}
                                        </>
                                    ) : (
                                        dish.status === OrderItemStatus.Storno && (
                                            <div style={{ color: theme.palette.error.main }}>
                                                / STORNO
                                            </div>
                                        )
                                    )}
                                </div>
                            </div>
                            {hasModifiedIngredients(dish) &&
                                renderEditedIngredients(dish, dailyMenu ? dailyMenu.id : undefined)}
                        </div>
                    );
                })}
                {renderComment(dailyMenu!, {
                    marginTop: 8,
                    paddingRight: 14,
                    paddingLeft: 20,
                })}
            </Card>
            {orderDetails.service === OrderServices.DineIn &&
                ((restaurant.isDeliverAnytime && currentPosition !== Positions.MANAGER) ||
                    (dishes.some((dish) => dish.status === OrderItemStatus.Ready) &&
                        currentPosition !== Positions.MANAGER &&
                        orderDetails.responsibleUserId === user.authUserId) ||
                    dishes.some((dish) =>
                        [OrderItemStatus.Delivered, OrderItemStatus.Closed].includes(dish.status)
                    )) && (
                    <Checkbox
                        style={{ paddingRight: 0, visibility: 'hidden' }}
                        inputProps={{ 'aria-label': 'select all desserts' }}
                        disabled
                    />
                )}

            <Menu
                id="daily-menu-print-menu"
                anchorEl={printAnchorEl}
                keepMounted
                open={Boolean(printItem)}
                onClose={handlePrintClose}
            >
                <MenuItem onClick={() => handlePrint(PrintType.SingleItem)}>
                    {i18n.t('common.item')}
                </MenuItem>
                <MenuItem onClick={() => handlePrint(PrintType.WholeOrder)}>
                    {i18n.t('common.wholeOrder')}
                </MenuItem>
            </Menu>
        </div>
    );
}
